<script setup>
import { onMounted, ref } from 'vue';
import { useStudent } from '@/features/student/useStudent';
import { useRoute, useRouter } from 'vue-router';
import { $api } from '@/services';
import Checkbox from '@/components/elements/checkbox/Checkbox';
import Multiselect from '@suadelabs/vue3-multiselect';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import CompleteRecommend from './CompleteRecommend/CompleteRecommend.vue';
import { useStore } from 'vuex';
import EditGoal from './EditGoal/EditGoal.vue';
import { useTutorial } from '@/features/tutorial/useTutorial';
import TermTooltip from '@/components/elements/termTooltip/TermTooltip.vue';
import {
	TUTORIAL_VIRTUAL_GOAL_LIBRARY,
	TUTORIAL_VIRTUAL_LIBRARIES,
	TUTORIAL_VIRTUAL_LIBRARY_CREATE,
	TUTORIAL_VIRTUAL_SKILLS,
	TUTORIAL_VIRTUAL_STUDENT,
	isTutorialVirtualStudent,
} from '@/features/tutorial/virtualStudent';

const { getStudent } = useStudent();
const route = useRoute();
const router = useRouter();
const id = ref(route.params.id);
const student = ref(null);
const listLibrary = ref([]);
const goalCreate = ref(15);
const noLimit = ref(false);
const library = ref([]);
const libraryIds = ref([]);
const step = ref(1);
const libraryData = ref(null);
const numbers = ref([...Array(11).keys()]);
const selectedAgeRange = ref([]);
const libraryCreate = ref([]);
const store = useStore();
const modalIsOpen = ref(false);
const goalLibrary = ref(null);
const goal = ref(null);
const sideOverShow = ref(false);
const skillName = ref('');
const {
	phase,
	activeTask,
	activeSpotlight,
	setSpotlightByTarget,
	completeCurrentTask,
} = useTutorial();

function isCreatePlanTour() {
	return (
		phase.value === 'tour' &&
		activeTask.value?.key === 'edit_plan_with_ai'
	);
}

onMounted(() => {
	fetchStudent();
	fetchLibrary();
});

const fetchStudent = async () => {
	if (isTutorialVirtualStudent(id.value)) {
		student.value = { ...TUTORIAL_VIRTUAL_STUDENT };
		return;
	}
	const res = await getStudent(id.value);
	if (res?.data) {
		student.value = res?.data;
	}
};

const fetchLibrary = async () => {
	if (isTutorialVirtualStudent(id.value)) {
		listLibrary.value = TUTORIAL_VIRTUAL_LIBRARIES;
		return;
	}
	try {
		const res = await $api.goal.listLibraryWithAge();
		if (res.data) listLibrary.value = res.data.data;
		// isLoading.value = false;
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};

const toStep2 = async () => {
	if (isTutorialVirtualStudent(id.value)) {
		libraryData.value = TUTORIAL_VIRTUAL_SKILLS.map((skill) => ({
			...skill,
		}));
		selectedAgeRange.value = libraryData.value.map(
			(skill) => skill.age_ranges[skill.selected_option]
		);
		goalLibrary.value = { ...TUTORIAL_VIRTUAL_GOAL_LIBRARY };
		step.value = 2;
		if (isCreatePlanTour()) {
			setSpotlightByTarget('rec-priority');
		}
		return;
	}
	try {
		let params = {
			student_id: id.value,
			goal_library_ids: libraryIds.value,
		};
		if (noLimit.value) {
			params.no_limit = true;
		}
		const res = await $api.goal.listLibraryWithAgeDetail(params);
		if (res.data) {
			libraryData.value = res.data.data;
			selectedAgeRange.value = libraryData.value.map(
				(skill) => skill.age_ranges[skill.selected_option]
			);
			goalLibrary.value = res.data.goal_libraries;
			step.value = 2;
		}
		// isLoading.value = false;
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};

const onAgeSelected = (skillIndex, selected, skill) => {
	const ageIndex = skill.age_ranges.findIndex(
		(age) => age.display_age === selected.display_age
	);
	if (ageIndex !== -1) {
		skill.selected_option = ageIndex;
	}
};

const toStep3 = async () => {
	if (isTutorialVirtualStudent(id.value)) {
		libraryCreate.value = JSON.parse(
			JSON.stringify(TUTORIAL_VIRTUAL_LIBRARY_CREATE)
		);
		step.value = 3;
		if (isCreatePlanTour()) {
			setSpotlightByTarget('rec-edit-goals');
		}
		return;
	}
	try {
		const skills_with_age_ranges = libraryData.value.map((skill) => {
			// Extract 'age_ranges' to remove it, and collect everything else into 'rest'
			const { age_ranges, ...rest } = skill;

			return {
				...rest, // Spread all properties EXCEPT age_ranges
				selected_option_object: age_ranges[skill.selected_option],
			};
		});
		const res = await $api.goal.createListLibraryWithAgeDetail(
			id.value,

			{
				goal_library_ids: libraryIds.value,
				skills_with_age_ranges: skills_with_age_ranges.filter(
					(skill) => skill.priority_level > 0
				),
				number_of_library_items: goalCreate.value,
				no_limit: noLimit.value,
			}
		);
		if (res.data) {
			libraryCreate.value = res.data.data;
			step.value = 3;
		}
		// isLoading.value = false;
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
const complete = async (name) => {
	if (isTutorialVirtualStudent(id.value)) {
		if (isCreatePlanTour()) {
			await completeCurrentTask();
		}
		router.push(`/plans/${id.value}/goal-bank`);
		return;
	}
	try {
		const res = await $api.goal.createFromSuggestion(libraryIds.value, {
			goal_library_ids: libraryIds.value,
			student_id: id.value,
			skills_with_age_ranges: libraryCreate.value,
			group: {
				name: name,
			},
		});
		if (res.data) {
			await store.dispatch('setAlertMessage', {
				message: 'Thêm mục tiêu vào ngân hàng mục tiêu thành công',
			});
			router.push(`/plans/${id.value}/goal-bank`);
		}
		// isLoading.value = false;
	} catch (e) {
		await store.dispatch('setAlertMessage', {
			message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};

const changeLimit = () => {
	noLimit.value = !noLimit.value;
	if (noLimit.value && libraryIds.value.length) {
		goalCreate.value = library.value.goal_count;
	}
};

const editGoal = (data, skill) => {
	goal.value = data;
	skillName.value = skill;
	sideOverShow.value = true;
};

const toggleSelect = (item) => {
	library.value = item;
	libraryIds.value = item.ids;
	if (noLimit.value) {
		goalCreate.value = item.goal_count;
	}
	if (
		isCreatePlanTour() &&
		activeSpotlight.value?.targetId === 'rec-library-list'
	) {
		setSpotlightByTarget('rec-step2-btn');
	}
};

const openCompleteModal = () => {
	modalIsOpen.value = true;
	if (isCreatePlanTour()) {
		setSpotlightByTarget('rec-name-form');
	}
};
</script>
<template>
	<div :class="sideOverShow ? 'side-over--active' : ''" v-if="sideOverShow">
		<div
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="sideOverShow = false"
		></div>
		<EditGoal
			@close="sideOverShow = false"
			:sideOverShow="sideOverShow"
			:data="goal"
			:skillName="skillName"
		/>
	</div>
	<DaModalFrame v-model:open="modalIsOpen">
		<CompleteRecommend
			data-tour-id="rec-name-form"
			@close="modalIsOpen = false"
			@submit="complete"
		/>
	</DaModalFrame>
	<div class="w-full pb-10">
		<div class="__goal-management-student-detail p-10 md-down:px-4">
			<div class="flex items-center justify-between">
				<div
					class="__breadcrumbs text-sm text-neutral-500 font-semibold flex-nowrap flex items-center"
				>
					Ngân hàng mục tiêu
				</div>
				<button
					type="button"
					class="button-p text-center w-fit btn--text text-neutral-500 hover:text-neutral-600"
					@click="$router.push(`/plans/${id}/goal-bank`)"
				>
					<svg
						width="16"
						height="16"
						viewBox="0 0 16 16"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						class="mr-2 text-neutral-400"
					>
						<path
							d="M1.5 7.99996L0.719131 7.37526L0.219375 7.99996L0.719131 8.62465L1.5 7.99996ZM7.5 8.99996C8.05228 8.99996 8.5 8.55224 8.5 7.99996C8.5 7.44767 8.05228 6.99996 7.5 6.99996V8.99996ZM3.3858 4.04193L0.719131 7.37526L2.28087 8.62465L4.94754 5.29132L3.3858 4.04193ZM0.719131 8.62465L3.3858 11.958L4.94754 10.7086L2.28087 7.37526L0.719131 8.62465ZM1.5 8.99996H7.5V6.99996H1.5V8.99996Z"
							fill="currentColor"
						/>
						<path
							d="M7.78738 13.3439L8.31032 12.4915L7.78738 13.3439ZM6.97976 12.3905L6.05301 12.7662L6.97976 12.3905ZM14.499 12.5015L15.3875 12.9603L14.499 12.5015ZM12.7525 13.981L12.4461 13.0291L12.7525 13.981ZM14.499 3.49854L15.3875 3.0398L14.499 3.49854ZM13.8333 6.81921V9.18087H15.8333V6.81921H13.8333ZM7.83325 5.42133V5.29122H5.83325V5.42133H7.83325ZM9.58524 12.7782C8.62733 12.6186 8.43551 12.5683 8.31032 12.4915L7.26445 14.1962C7.80406 14.5273 8.47961 14.6216 9.25644 14.751L9.58524 12.7782ZM5.83325 10.7107C5.83325 11.4979 5.81518 12.1795 6.05301 12.7662L7.90651 12.0148C7.85132 11.8787 7.83325 11.6814 7.83325 10.7107H5.83325ZM8.31032 12.4915C8.12826 12.3798 7.98675 12.2128 7.90651 12.0148L6.05301 12.7662C6.29374 13.36 6.71828 13.8612 7.26445 14.1962L8.31032 12.4915ZM13.8333 9.18087C13.8333 10.0882 13.8325 10.7099 13.7946 11.1876C13.7573 11.6573 13.6894 11.8898 13.6104 12.0428L15.3875 12.9603C15.6428 12.4658 15.742 11.929 15.7883 11.3458C15.834 10.7705 15.8333 10.0557 15.8333 9.18087H13.8333ZM9.25644 14.751C10.1198 14.8949 10.8251 15.0132 11.4002 15.0628C11.9832 15.113 12.5291 15.1035 13.0589 14.9329L12.4461 13.0291C12.2822 13.0819 12.0416 13.1107 11.5719 13.0702C11.0943 13.029 10.4806 12.9275 9.58524 12.7782L9.25644 14.751ZM13.6104 12.0428C13.3679 12.5125 12.9493 12.8671 12.4461 13.0291L13.0589 14.9329C14.0653 14.609 14.9025 13.8997 15.3875 12.9603L13.6104 12.0428ZM9.58645 3.22163C10.4814 3.07248 11.0948 2.97099 11.5722 2.92986C12.0416 2.88942 12.2822 2.91819 12.4461 2.97096L13.0589 1.06717C12.5292 0.896646 11.9834 0.887021 11.4005 0.937243C10.8256 0.986778 10.1206 1.10503 9.25766 1.24885L9.58645 3.22163ZM15.8333 6.81921C15.8333 5.9444 15.834 5.22954 15.7883 4.65428C15.742 4.07104 15.6428 3.53429 15.3875 3.0398L13.6104 3.95728C13.6894 4.1103 13.7573 4.34283 13.7946 4.81252C13.8325 5.29019 13.8333 5.91191 13.8333 6.81921H15.8333ZM12.4461 2.97096C12.9493 3.13294 13.3679 3.48755 13.6104 3.95728L15.3875 3.0398C14.9025 2.10035 14.0653 1.39113 13.0589 1.06717L12.4461 2.97096ZM9.25766 1.24885C8.83685 1.31898 8.47124 1.37935 8.17099 1.45102C7.85987 1.52528 7.55473 1.62545 7.26252 1.80503L8.30967 3.50898C8.35038 3.48397 8.42887 3.44565 8.63532 3.39637C8.85265 3.3445 9.13857 3.29628 9.58645 3.22163L9.25766 1.24885ZM7.83325 5.29122C7.83325 4.83715 7.83381 4.5472 7.84925 4.3243C7.86391 4.11256 7.88881 4.02884 7.90679 3.98457L6.05387 3.23179C5.92477 3.54955 5.87613 3.86701 5.85403 4.1861C5.8327 4.49405 5.83325 4.86461 5.83325 5.29122H7.83325ZM7.26252 1.80503C6.71792 2.13971 6.29446 2.63958 6.05387 3.23179L7.90679 3.98457C7.98699 3.78717 8.12814 3.62054 8.30967 3.50898L7.26252 1.80503Z"
							fill="currentColor"
						/>
					</svg>
					Thoát
				</button>
			</div>
			<div class="__goal-management-student-detail__header pt-5">
				<div
					class="flex justify-between items-center w-full flex-wrap gap-8"
				>
					<div class="flex items-center">
						<img
							style="min-width: 56px"
							class="shadow-sm inline-block h-14 w-14 rounded-full"
							:src="student?.profile_photo?.w200"
							v-if="student?.profile_photo?.w200"
						/>
						<span
							style="min-width: 56px"
							v-else
							class="inline-flex items-center justify-center h-14 w-14 rounded-full"
							:style="{
								background:
									student?.profile_photo?.default?.color,
							}"
						>
							<span
								class="font-medium leading-none text-white text-4xl"
								>{{
									student?.profile_photo?.default?.initial
								}}</span
							>
						</span>
						<div class="ml-4">
							<div
								class="font-bold text-neutral-700 text-4xl md-down:text-2xl"
							>
								{{ student?.name }}
							</div>
							<div
								v-if="student?.age"
								class="text-green-500 font-semibold text-xs uppercase"
							>
								{{ student?.age }}
								{{
									student?.learning_time
										? `| ${student?.learning_time}`
										: ''
								}}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="px-8 md-down:px-4 pb-8">
			<div class="relative">
				<div
					aria-hidden="true"
					class="absolute inset-0 flex items-center"
				>
					<div class="w-full border-t border-neutral-300" />
				</div>
				<div
					class="relative flex items-center justify-between"
					v-if="step === 1"
				>
					<span
						class="pr-3 bg-body text-lg font-semibold text-neutral-600 mb-2"
					>
						Bước 1: Thư viện mục tiêu
					</span>
					<button
						data-tour-id="rec-step2-btn"
						:disabled="!libraryIds.length || goalCreate === 0"
						class="button-p text-center whitespace-nowrap w-fit md-down:hidden flex items-center gap-1"
						type="button"
						@click="toStep2"
					>
						<img
							v-if="!libraryIds.length || goalCreate === 0"
							src="@/assets/images/icons/gray-chevron-right.png"
							alt=""
						/>
						<img
							v-else
							src="@/assets/images/icons/chevron-right.png"
							alt=""
						/>
						Bước 2: Lĩnh vực can thiệp
					</button>
				</div>
				<div
					class="relative flex items-center justify-between"
					v-if="step === 2"
				>
					<div
						class="pr-3 bg-body text-lg font-semibold text-neutral-600 mb-2 flex items-center gap-2"
					>
						<img
							@click="step = 1"
							class="rotate-image cursor-pointer"
							src="@/assets/images/icons/chevron-right.png"
							alt=""
						/>
						<div>Bước 2: Lĩnh vực can thiệp</div>
					</div>
					<button
						data-tour-id="rec-step3-btn"
						class="button-p text-center whitespace-nowrap w-fit md-down:hidden flex items-center gap-1"
						type="button"
						@click="toStep3"
					>
						<img
							src="@/assets/images/icons/chevron-right.png"
							alt=""
						/>
						<div>Bước 3: Kiểm tra và hoàn tất</div>
					</button>
				</div>
				<div
					class="relative flex items-center justify-between"
					v-if="step === 3"
				>
					<div
						class="pr-3 bg-body text-lg font-semibold text-neutral-600 mb-2 flex items-center gap-2"
					>
						<img
							@click="step = 2"
							class="rotate-image cursor-pointer"
							src="@/assets/images/icons/chevron-right.png"
							alt=""
						/>
						<div>Bước 3: Kiểm tra và hoàn tất</div>
					</div>
					<button
						data-tour-id="rec-complete-btn"
						class="button-p text-center whitespace-nowrap w-fit md-down:hidden flex items-center gap-1"
						type="button"
						@click="openCompleteModal"
					>
						Hoàn thành
					</button>
				</div>
			</div>
			<div class="mt-10 w-4/5 md-down:w-full m-auto">
				<div v-if="step === 1">
					<div
						class="
							flex
							items-center
							justify-between
							md-down:flex-col
							md-down:items-start
							md-down:gap-3
						"
					>
						<div
							class="
								text-gray-600
								font-semibold
								text-lg
								md-down:text-base
							"
						>
							Tổng số mục tiêu muốn tạo
						</div>
						<div class="flex items-center" data-tour-id="rec-goal-count">
							<button
								:disabled="goalCreate === 0 || noLimit"
								@click="goalCreate--"
								class="flex items-center justify-center minus-button w-11 h-11"
							>
								<svg
									width="14"
									height="2"
									viewBox="0 0 14 2"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M1 1H13"
										stroke="#94A3B8"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</button>
							<div class="search-input w-11 h-11">
								<div class="input-group">
									<input
										:disabled="noLimit"
										v-model="goalCreate"
										class="text-gray-700 goal-create"
										type="number"
									/>
								</div>
							</div>
							<button
								:disabled="noLimit"
								@click="goalCreate++"
								class="flex items-center justify-center plus-button w-11 h-11"
							>
								<svg
									width="14"
									height="14"
									viewBox="0 0 14 14"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M7 1V13"
										stroke="#94A3B8"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
									<path
										d="M1 7H13"
										stroke="#94A3B8"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</button>
						</div>
					</div>
					<div
						class="flex items-center gap-2 cursor-pointer mt-2"
						@click="changeLimit"
					>
						<Checkbox :value="noLimit" />
						<div class="text-neutral-700">
							Không giới hạn số mục tiêu
						</div>
					</div>
					<div class="mt-4">
						<div
							class="
								text-gray-600
								font-semibold
								text-lg
								md-down:text-base
							"
						>
							Chọn thư viện mục tiêu
						</div>
						<div class="overflow-x-auto scroll-bar" data-tour-id="rec-library-list">
						<table
							class="min-w-full divide-y text-neutral-200 mt-4 table-library"
						>
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									></th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									>
										STT
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
										style="min-width: 160px"
									>
										tên thư viện
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									>
										Lĩnh vực
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									>
										mục tiêu
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									>
										độ tuổi
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(l, i) in listLibrary"
									:key="i"
									:class="
										i % 2 === 0
											? 'bg-white'
											: 'bg-neutral-50'
									"
									class="text-neutral-700 hover:bg-orange-50 cursor-pointer"
									@click.stop="toggleSelect(l)"
								>
									<td
										class="px-6 py-4 whitespace-nowrap text-neutral-700 text-center"
									>
										<input
											type="radio"
											:value="l.ids"
											v-model="libraryIds"
											class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600"
										/>
									</td>
									<td
										class="px-6 py-4 whitespace-nowrap text-neutral-700 text-center"
									>
										{{ i + 1 }}
									</td>
									<td class="px-6 py-4">
										<div class="flex items-center gap-2">
											<img
												src="@/assets/images/icons/library.png"
												alt=""
											/>
											<div class="text-gray-700">
												{{ l.name }}
											</div>
										</div>
										<div
											class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
											style="font-size: 10px"
											v-if="l?.age"
										>
											{{ l?.age }}
										</div>
									</td>
									<td
										class="px-6 py-4 text-neutral-700 text-center"
									>
										{{ l.skill_count }}
									</td>
									<td
										class="px-6 py-4 text-neutral-700 text-center"
									>
										{{ l.goal_count }}
									</td>
									<td
										class="px-6 py-4 text-neutral-700 whitespace-nowrap"
									>
										{{ l.display_age_range }}
									</td>
								</tr>
							</tbody>
						</table>
						</div>
					</div>
				</div>
				<div v-if="step === 2 || step === 3" class="mb-4">
					<div class="text-gray-600 font-semibold">
						{{ goalLibrary.name }}
					</div>
					<div class="text-gray-700 mt-2">
						{{ goalLibrary.description }}
					</div>
				</div>
				<div v-if="step === 2">
					<div class="overflow-x-auto scroll-bar">
					<table
						class="min-w-full divide-y text-neutral-200 mt-4 table-library"
					>
						<thead class="bg-neutral-100 h-16">
							<tr>
								<th
									class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
									scope="col"
								>
									STT
								</th>
								<th
									class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
									scope="col"
									style="min-width: 160px"
								>
									lĩnh vực
								</th>
								<th
									class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
									scope="col"
									style="width: 275px"
								>
									<span class="inline-flex items-center gap-1">
										mức độ ưu tiên
										<TermTooltip
											text="Lựa chọn Mức độ ưu tiên cho từng lĩnh vực. Mức 0 sẽ không trả về mục tiêu nào của lĩnh vực đó"
											label="Giải thích mức độ ưu tiên"
										/>
									</span>
								</th>
								<th
									class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
									scope="col"
									style="width: 275px"
								>
									<span class="inline-flex items-center gap-1">
										tháng tuổi
										<TermTooltip
											text="Lựa chọn Độ tuổi cho lĩnh vực. Thông thường mỗi thư viện chỉ có 1 mức tuổi, nếu có nhiều hơn, bạn có thể lựa chọn mức tuổi phù hợp cho lĩnh vực."
											label="Giải thích tháng tuổi"
										/>
									</span>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(l, index) in libraryData"
								:key="l.id"
								:class="
									index % 2 === 0
										? 'bg-white'
										: 'bg-neutral-50'
								"
								class="text-neutral-700 hover:bg-orange-50 cursor-pointer"
							>
								<td
									class="px-6 py-4 whitespace-nowrap text-neutral-700 text-center"
								>
									{{ index + 1 }}
								</td>
								<td class="px-6 py-4 text-gray-700">
									<div>{{ l.name }}</div>
								</td>
								<td
									class="px-6 py-4 text-neutral-700 priority"
									:data-tour-id="index === 0 ? 'rec-priority' : undefined"
								>
									<Multiselect
										:options="numbers"
										v-model="l.priority_level"
										:allow-empty="false"
										placeholder="Chọn mức độ ưu tiên"
										deselect-label=""
										selectLabel=""
									>
									</Multiselect>
								</td>
								<td
									class="px-6 py-4 text-neutral-700 ]"
									:data-tour-id="index === 0 ? 'rec-age' : undefined"
								>
									<Multiselect
										:options="l.age_ranges"
										v-model="selectedAgeRange[index]"
										:searchable="true"
										:allow-empty="false"
										placeholder="Chọn tháng tuổi"
										deselect-label=""
										selectLabel=""
										label="display_age"
										track-by="display_age"
										@update:modelValue="
											onAgeSelected(index, $event, l)
										"
									>
									</Multiselect>
								</td>
							</tr>
						</tbody>
					</table>
					</div>
				</div>
				<div v-if="step === 3" data-tour-id="rec-filter-goals">
					<div v-for="(l, index) in libraryCreate" :key="l.id">
						<div class="text-gray-600 font-semibold md-down:text-base">
							{{ index + 1 }}. {{ l.name }} ({{ l.item_count }})
						</div>
						<div class="overflow-x-auto scroll-bar">
						<table
							class="min-w-full divide-y text-neutral-200 my-4 table-library"
						>
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										v-if="
											l.library_items.some(
												(i) => i.sub_item_count > 0
											)
										"
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									></th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
										scope="col"
									>
										STT
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
										style="min-width: 160px"
									>
										Mục tiêu
									</th>
								</tr>
							</thead>
							<tbody>
								<template
									v-for="(i, index) in l.library_items"
									:key="i.id"
								>
									<tr
										:class="
											index % 2 === 0
												? 'bg-white'
												: 'bg-neutral-50'
										"
										class="text-neutral-700 hover:bg-orange-50 cursor-pointer"
									>
										<td
											v-if="i.sub_item_count > 0"
											class="px-6 py-4 w-8"
											@click.stop="i.open = !i.open"
										>
											<div class="w-4">
												<img
													class="w-full"
													v-if="i.open"
													src="@/assets/images/icons/arrow-up.png"
													alt=""
												/>
												<img
													class="w-full"
													v-else
													src="@/assets/images/icons/arrow-down.png"
													alt=""
												/>
											</div>
										</td>
										<td
											class="px-6 py-4 whitespace-nowrap text-neutral-700 text-center"
										>
											{{ index + 1 }}
										</td>
										<td class="px-6 py-4 text-gray-700">
											<div
												class="flex items-center justify-between"
											>
												<div class="w-4/5">
													<div>{{ i.name }}</div>
													<div
														class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
														style="font-size: 10px"
														v-if="i?.age"
													>
														{{ i?.age }}
													</div>
												</div>
												<div
													v-if="!i.sub_item_count"
													class="flex items-center gap-3"
												>
													<img
														:data-tour-id="
															index === 0
																? 'rec-edit-goals'
																: undefined
														"
														@click="
															editGoal(i, l.name)
														"
														src="@/assets/images/icons/edit-fill.png"
														alt=""
													/>
													<Checkbox
														@click="
															i.selected =
																!i.selected
														"
														:value="i.selected"
													/>
												</div>
											</div>
										</td>
									</tr>
									<tr
										v-if="i.open && i.sub_item_count > 0"
										class="text-neutral-700 cursor-pointer"
										:class="
											index % 2 === 0
												? 'bg-white'
												: 'bg-neutral-50'
										"
									>
										<td colspan="3" class="pl-16 md-down:pl-6">
											<div class="overflow-x-auto scroll-bar">
											<table
												class="min-w-full divide-y text-neutral-200 my-4 table-library"
											>
												<thead
													class="bg-neutral-100 h-16"
												>
													<tr>
														<th
															class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap"
															scope="col"
														>
															STT
														</th>
														<th
															class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
															scope="col"
															style="
																min-width: 160px;
															"
														>
															Mục tiêu
														</th>
													</tr>
												</thead>
												<tbody>
													<template
														v-for="(
															subItem, subIndex
														) in i.sub_items"
														:key="subIndex"
													>
														<tr
															:class="
																subIndex % 2 ===
																0
																	? 'bg-white'
																	: 'bg-neutral-50'
															"
															class="text-neutral-700 hover:bg-orange-50 cursor-pointer"
														>
															<td
																class="px-6 py-4 whitespace-nowrap text-neutral-700 text-center"
															>
																{{
																	subIndex + 1
																}}
															</td>
															<td
																class="px-6 py-4 text-gray-700"
															>
																<div
																	class="flex items-center justify-between"
																>
																	<div
																		class="w-4/5"
																	>
																		<div>
																			{{
																				subItem.name
																			}}
																		</div>
																		<div
																			class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
																			style="
																				font-size: 10px;
																			"
																			v-if="
																				subItem?.age
																			"
																		>
																			{{
																				subItem?.age
																			}}
																		</div>
																	</div>

																	<div
																		class="flex items-center gap-3"
																	>
																		<img
																			@click="
																				editGoal(
																					subItem,
																					l.name
																				)
																			"
																			src="@/assets/images/icons/edit-fill.png"
																			alt=""
																		/>
																		<Checkbox
																			@click="
																				subItem.selected =
																					!subItem.selected
																			"
																			:value="
																				subItem.selected
																			"
																		/>
																	</div>
																</div>
															</td>
														</tr>
													</template>
												</tbody>
											</table>
											</div>
										</td>
									</tr>
								</template>
							</tbody>
						</table>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			v-if="step === 1"
			class="
				md:hidden
				fixed
				bottom-0
				left-0
				right-0
				p-4
				bg-white
				border-t
				border-neutral-200
				z-20
			"
		>
			<button
				:disabled="!libraryIds.length || goalCreate === 0"
				class="button-p text-center whitespace-nowrap w-full"
				type="button"
				@click="toStep2"
			>
				Bước 2: Lĩnh vực can thiệp
			</button>
		</div>
		<div
			v-if="step === 2"
			class="
				md:hidden
				fixed
				bottom-0
				left-0
				right-0
				p-4
				bg-white
				border-t
				border-neutral-200
				z-20
			"
		>
			<button
				class="button-p text-center whitespace-nowrap w-full"
				type="button"
				@click="toStep3"
			>
				Bước 3: Kiểm tra và hoàn tất
			</button>
		</div>
		<div
			v-if="step === 3"
			class="
				md:hidden
				fixed
				bottom-0
				left-0
				right-0
				p-4
				bg-white
				border-t
				border-neutral-200
				z-20
			"
		>
			<button
				class="button-p text-center whitespace-nowrap w-full"
				type="button"
				@click="modalIsOpen = true"
			>
				Hoàn thành
			</button>
		</div>
	</div>
</template>
<style scoped>
.minus-button {
	border: 1px solid #e1e7ef;
	border-top-left-radius: 8px;
	border-bottom-left-radius: 8px;
}
.goal-create {
	border-top: 1px solid #e1e7ef;
	border-bottom: 1px solid #e1e7ef;
	background: #f1f5f9;
	height: 44px !important;
	border-radius: 0 !important;
	border-left: none;
	border-right: none;
	padding: 0 !important;
	text-align: center;
}
.plus-button {
	border: 1px solid #e1e7ef;
	border-top-right-radius: 8px;
	border-bottom-right-radius: 8px;
}
.table-library {
	box-shadow: 0px 3px 8px -1px #3232470d;
	box-shadow: 0px 0px 1px 0px #0c1a4b3d;
}
</style>
<style>
.priority > .multiselect--disabled > .multiselect__tags > .multiselect__single {
	background: #f1f5f9;
}
.rotate-image {
	rotate: 180deg;
}
</style>
